import React, { useState, useEffect } from "react";
import {
  Home,
  Users,
  Star,
  FileText,
  CreditCard,
  Settings,
  Building2,
  MapPin,
  Phone,
  Mail,
  Camera,
  Calendar,
  TrendingUp,
  IndianRupee,
  Eye,
  Bell,
  Menu,
  X,
  Upload,
  Plus,
  Edit3,
  Save,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  Globe,
  Shield,
  Award,
  Search,
  Navigation,
  Loader2,
  Image as ImageIcon,
  VideoIcon,
  LogOut,
} from "lucide-react";

import { toast } from "react-hot-toast";
import api from "../../utils/axios";
import { useAuth } from "../../store/hooks/useRedux";


const BusinessProfile = ({ vendorData , onUpdate }) => {
    console.log("Vendor Data in BusinessProfile:", vendorData?.vendor);
    const [formData, setFormData] = useState({
      business_name: "",
      address: {
        street: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
      businessLocation: {
        type: "Point",
        coordinates: [],
      },
      contactDetails: {
        name: "",
        email: "",
        phone: "",
      },
      paymentMethods: {
        cash: false,
        card: false,
        upi: false,
      },
      profileImages: [],
    });
  
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [errors, setErrors] = useState({});
  
    useEffect(() => {
      if (vendorData?.vendor) {
        setFormData({
          business_name: vendorData?.vendor.business_name || "",
          address: vendorData?.vendor.address || {
            street: "",
            city: "",
            state: "",
            pincode: "",
            country: "India",
          },
          businessLocation: vendorData?.vendor.businessLocation || {
            type: "Point",
            coordinates: [],
          },
          contactDetails: vendorData?.vendor.contactDetails || {
            name: "",
            email: "",
            phone: "",
          },
          paymentMethods: vendorData?.vendor.paymentMethods || {
            cash: false,
            card: false,
            upi: false,
          },
          profileImages: vendorData?.vendor.profileImages || [],
        });
      }
    }, [vendorData?.vendor]);
  
    const handleImageUpload = async (files) => {
      try {
        setUploadingImages(true);
        const uploadFormData = new FormData();
        Array.from(files).forEach((file) => {
          uploadFormData.append("files", file);
        });
  
        const response = await api.post("/image/upload", uploadFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
  
        if (response.data.success) {
          const newImages = response.data.data.files.map((file) => ({
            url: file.url,
            caption: file.name,
          }));
  
          setFormData({
            ...formData,
            profileImages: [...formData.profileImages, ...newImages],
          });
          toast.success("Images uploaded successfully");
        }
      } catch (error) {
        toast.error("Failed to upload images");
      } finally {
        setUploadingImages(false);
      }
    };
  
    const getCurrentLocation = async () => {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
  
        const { latitude, longitude } = position.coords;
        const response = await api.get(
          `/users/address?lat=${latitude}&lng=${longitude}`
        );
  
        if (response.data.success) {
          setFormData({
            ...formData,
            businessLocation: {
              type: "Point",
              coordinates: [longitude, latitude],
            },
            address: {
              ...formData.address,
              ...response.data.data,
            },
          });
          toast.success("Location set successfully");
        }
      } catch (error) {
        toast.error("Failed to get location");
      }
    };
  
    const updateProfile = async () => {
      try {
        setLoading(true);
        setErrors({});
  
        const response = await api.put("/vendors/profile/update", formData);
  
        if (response.data.success) {
          toast.success("Profile updated successfully!");
          setEditMode(false);
          onUpdate();
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to update profile";
        setErrors({ general: errorMessage });
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
  
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-['Manrope'] text-xl font-bold tracking-tight text-gray-900">
                Business Profile
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage your business information and settings
              </p>
            </div>
  
            <div className="flex items-center space-x-3">
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white h-11 px-5 rounded-xl text-sm font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg transition-all"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setFormData(vendorData?.vendor);
                      setEditMode(false);
                      setErrors({});
                    }}
                    className="flex items-center space-x-2 bg-white text-gray-700 border border-gray-200 h-11 px-5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-all"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={updateProfile}
                    disabled={loading}
                    className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white h-11 px-5 rounded-xl text-sm font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg disabled:opacity-50 transition-all"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{loading ? "Saving..." : "Save Changes"}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
  
        {/* Error Display */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{errors.general}</span>
          </div>
        )}
  
        {/* Business Images */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center space-x-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50"><Camera className="w-[18px] h-[18px] text-purple-600" /></span>
              <h3 className="font-['Manrope'] text-base font-bold text-gray-900">
                Business Photos
              </h3>
            </div>
  
            {editMode && (
              <label className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:from-purple-700 hover:to-pink-700">
                {uploadingImages ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
                <span>Upload Photos</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files)}
                  className="hidden"
                />
              </label>
            )}
          </div>
  
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {formData.profileImages?.map((image, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={image.url}
                  alt={image.caption}
                  className="w-full h-32 object-cover rounded-lg"
                />
                {editMode && (
                  <button
                    type="button"
                    onClick={() => {
                      const newImages = formData.profileImages.filter(
                        (_, i) => i !== idx
                      );
                      setFormData({ ...formData, profileImages: newImages });
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
  
            {formData.profileImages?.length === 0 && (
              <div className="col-span-full flex items-center justify-center h-32 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/60">
                <div className="text-center">
                  <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    No business photos uploaded
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
  
        {/* Basic Information */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6">
          <div className="flex items-center space-x-2.5 mb-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50"><Building2 className="w-[18px] h-[18px] text-purple-600" /></span>
            <h3 className="font-['Manrope'] text-base font-bold text-gray-900">
              Basic Information
            </h3>
          </div>
  
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Business Name *
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) =>
                  setFormData({ ...formData, business_name: e.target.value })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="Enter your business name"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Contact Person *
              </label>
              <input
                type="text"
                value={formData.contactDetails?.name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contactDetails: {
                      ...formData.contactDetails,
                      name: e.target.value,
                    },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="Contact person name"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.contactDetails?.email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contactDetails: {
                      ...formData.contactDetails,
                      email: e.target.value,
                    },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="business@example.com"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.contactDetails?.phone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contactDetails: {
                      ...formData.contactDetails,
                      phone: e.target.value,
                    },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="+91 9876543210"
              />
            </div>
          </div>
        </div>
  
        {/* Business Location & Address */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center space-x-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50"><MapPin className="w-[18px] h-[18px] text-purple-600" /></span>
              <h3 className="font-['Manrope'] text-base font-bold text-gray-900">
                Business Location
              </h3>
            </div>
  
            {editMode && (
              <button
                type="button"
                onClick={getCurrentLocation}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700"
              >
                <Navigation className="w-4 h-4" />
                <span>Current Location</span>
              </button>
            )}
          </div>
  
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Street Address *
              </label>
              <input
                type="text"
                value={formData.address?.street}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="Enter street address"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                City *
              </label>
              <input
                type="text"
                value={formData.address?.city}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="City name"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                State *
              </label>
              <input
                type="text"
                value={formData.address?.state}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="State name"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                PIN Code *
              </label>
              <input
                type="text"
                value={formData.address?.pincode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, pincode: e.target.value },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="123456"
                maxLength="6"
              />
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Country
              </label>
              <input
                type="text"
                value={formData.address?.country}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value },
                  })
                }
                disabled={!editMode}
                className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all ${
                  !editMode ? "bg-gray-50" : "bg-white"
                }`}
                placeholder="Country name"
              />
            </div>
          </div>
  
          {formData.businessLocation?.coordinates?.length === 2 && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 text-green-700">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">
                  Location: {formData.businessLocation.coordinates[1].toFixed(6)},{" "}
                  {formData.businessLocation.coordinates[0].toFixed(6)}
                </span>
              </div>
            </div>
          )}
        </div>
  
        {/* Payment Methods */}
        {/* <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6">
          <div className="flex items-center space-x-2.5 mb-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50"><CreditCard className="w-[18px] h-[18px] text-purple-600" /></span>
            <h3 className="font-['Manrope'] text-base font-bold text-gray-900">
              Accepted Payment Methods
            </h3>
          </div>
  
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { key: "cash", label: "Cash Payments", icon: IndianRupee },
              { key: "card", label: "Card Payments", icon: CreditCard },
              { key: "upi", label: "UPI Payments", icon: Phone },
            ].map(({ key, label, icon: Icon }) => (
              <div
                key={key}
                className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg"
              >
                <input
                  type="checkbox"
                  checked={formData.paymentMethods?.[key] || false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      paymentMethods: {
                        ...formData.paymentMethods,
                        [key]: e.target.checked,
                      },
                    })
                  }
                  disabled={!editMode}
                  className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="text-gray-900 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div> */}
      </div>
    );
  };
  
  export default BusinessProfile;